import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getPixelRadius } from '../../utils/maps';

const MAP_CENTER = { lat: 63.176683, lng: 14.636068 };

const createMap = (
  element: HTMLDivElement,
  center: google.maps.LatLng,
  initialZoomLevel: number,
) => {
  if (!element) return null;

  return new window.google.maps.Map(element, {
    center,
    zoom: initialZoomLevel,
    disableDefaultUI: true,
    scrollwheel: false,
    zoomControl: false,
    mapTypeControl: false,
    scaleControl: false,
    clickableIcons: false,
    disableDoubleClickZoom: true,
    fullscreenControl: false,
    gestureHandling: 'greedy',
    keyboardShortcuts: false,
    maxZoom: 13,
  });
};
const createMarker = (map: google.maps.Map, center: google.maps.LatLng, radius: number) =>
  new google.maps.Circle({
    strokeColor: '#f65964',
    strokeWeight: 1,
    strokeOpacity: 1.0,
    fillColor: '#f65964',
    fillOpacity: 0.15,
    map,
    center,
    radius: radius * 1000,
  });

interface MapPosition {
  lat: number;
  lng: number;
}

interface SelectFilterLocationMapProps {
  position?: MapPosition;
  radius?: number;
  children?: React.ReactNode;
  onChange: (lat: number, lng: number) => void;
  onRequestCurrentPosition: () => void;
}

const SelectFilterLocationMap = ({
  position,
  radius,
  children,
  onChange,
  onRequestCurrentPosition,
}: SelectFilterLocationMapProps) => {
  const { t } = useTranslation('search');
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Circle | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout>(-1 as unknown as NodeJS.Timeout);

  const [state, setState] = useState({ active: false, dragging: false, dimensions: 0 });

  useEffect(() => {
    const emptyMap = !position;
    const location = emptyMap ? MAP_CENTER : position;
    if (mapContainerRef.current) {
      mapRef.current = createMap(
        mapContainerRef.current,
        new google.maps.LatLng(location.lat, location.lng),
        emptyMap ? 3 : 8,
      );

      if (mapRef.current) {
        const map = mapRef.current;
        map.addListener('dragstart', onDragStart);
        map.addListener('idle', onDragEnd);
        if (state.active && radius && position) {
          map.addListener('projection_changed', () => repaint(radius, position, true));
        }
      }
    }

    return () => {
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (position && radius) {
      repaint(radius, position, true);
    }
  }, [position?.lat, position?.lng]);

  useEffect(() => {
    if (position && radius) {
      repaint(radius, position, true);
    }
  }, [radius?.toString()]);

  const resetAspectRatio = () => {
    if (mapRef.current && markerRef.current) {
      const map = mapRef.current;
      const markerBounds = markerRef.current.getBounds();
      const markerCenter = markerRef.current.getCenter();
      if (markerBounds && markerCenter) {
        map.fitBounds(markerBounds);
        map.setCenter(markerCenter);
      }
    }
  };

  const repaint = (
    radius: number,
    position: MapPosition,
    shoudlResetAspectRatio = false,
    delay = false,
  ) => {
    if (!position || !mapRef.current) {
      return;
    }
    const latlng = new window.google.maps.LatLng(position.lat, position.lng);
    if (!markerRef.current) {
      markerRef.current = createMarker(mapRef.current, latlng, radius);
    } else {
      const marker = markerRef.current;
      marker.setCenter(latlng);
      marker.setRadius(radius * 1000);
    }

    if (shoudlResetAspectRatio) {
      if (delay) {
        // Used when search menu is open, then a location i selected.
        setTimeout(resetAspectRatio, 200);
      } else {
        resetAspectRatio();
      }
    }
  };

  const onDragStart = () => {
    if (mapRef.current && markerRef.current) {
      const map = mapRef.current;
      const marker = markerRef.current;
      clearTimeout(timeoutRef.current);
      if (!state.active) {
        const dimensions = getPixelRadius(map, marker) * 2;
        map.setOptions({
          minZoom: (map as unknown as { zoom: number }).zoom,
          maxZoom: (map as unknown as { zoom: number }).zoom,
        });
        marker.setMap(null);
        setState({ ...state, active: true, dragging: true, dimensions });
      }
    }
  };

  const onDragEnd = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (mapRef.current) {
        const map = mapRef.current;

        map.setOptions({ minZoom: null, maxZoom: 15 });
        if (markerRef.current) {
          const marker = markerRef.current;

          marker.setMap(map);
          const mapCenter = map.getCenter();
          if (mapCenter) {
            marker.setCenter(mapCenter);
          }
        }
        setState({ ...state, dragging: false, active: false });
        onPositionChange();
      }
    }, 500);
  };

  const onPositionChange = () => {
    if (!mapRef.current) return;

    if (markerRef.current && position) {
      const map = mapRef.current;
      const center = map.getCenter();
      const marker = markerRef.current;
      if (center) {
        marker.setCenter(center);
        if (
          (center.lat().toFixed(2) !== position.lat.toFixed(2) ||
            center.lng().toFixed(2) !== position.lng.toFixed(2)) &&
          radius
        ) {
          onChange(center.lat(), center.lng());
        }
      }
    }
  };

  const { active, dragging, dimensions } = state;
  const mapOverlayStyle = {
    display: active && dragging ? 'block' : 'none',
    width: `${dimensions}px`,
    height: `${dimensions}px`,
  };

  return (
    <div className="filter-map-container">
      <div className="filter-map" ref={mapContainerRef} />
      <div className="filter-map-overlay" style={mapOverlayStyle} />
      <div className="filter-map-tools">
        {children}
        {navigator.geolocation && (
          <div className="filter-map-tools-location">
            <button
              type="button"
              className="filter-map-current-location"
              onClick={onRequestCurrentPosition}
              title={t('Use current location', { ns: 'search' })}
            >
              <i className="icon-map-pin no-margin" />
            </button>
          </div>
        )}
      </div>
      <div className="filter-map-tooltip">
        {t('Drag the map to select a location', { ns: 'search' })}
      </div>
    </div>
  );
};

export default SelectFilterLocationMap;
